import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import Grid from "../../../components/Grid";
import { MoreHorizontal, Wallet, ChevronUp, ChevronDown, Search, ExternalLink } from "lucide-react";
import { ShowBtn } from "@/components/Show";
import { CreateLoaderInput } from "../../../components/Loaders";
import { DeleteBtn } from "@/components/Delete";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { ComponentSwitchToggleStatus } from "@/App/Courses/ActivedOrDisabled";
import { UpdateBtn } from "../Update";
import { TableDataProps } from "@/pages/Clients";
import { AddDevolution } from "@/App/Devolutions/Add";
import {
  setStatusClients,
  getShowClients,
  deleteClientsData,
  getClientBalance,
} from "@/Api/clients";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Mask } from "@/utils/mask";
import { Button } from "@/components/ui/button";
import { BalanceModal } from "@/components/ui/balance-modal";
import { useState } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { useTypeUser } from "@/hooks/auth";

interface TableDataExtendedProps {
  onSort?: (column: string, direction: "asc" | "desc") => void;
  sortConfig?: {
    column: string;
    direction: "asc" | "desc";
  };
  showDiscountColumn?: boolean;
}

export const TableData = ({ dataApi, pageActions, updateFront, onSort, sortConfig, showDiscountColumn }: any & TableDataExtendedProps) => {
  const [isBalanceModalOpen, setIsBalanceModalOpen] = useState(false);
  const authClientAndUnityResponsible = useTypeUser(2, 4);
  const authAllTypesUser = useTypeUser(2, 3, 4);
  const [selectedClient, setSelectedClient] = useState<{
    uuid: string;
    name: string;
    id?: string;
    total_balance?: number;
    has_purchase_voucher?: boolean;
  } | null>(null);
  const { hasPermission } = usePermissions();
  const handleOpenBalanceModal = async (client: {
    uuid: string;
    name: string;
    id?: string;
    has_purchase_voucher?: boolean;
  }) => {
    setSelectedClient(client);

    try {
      const { data } = await getClientBalance(client.uuid);
      if (data?.response) {
        setSelectedClient({
          ...client,
          total_balance: data?.response || 0,
        });
      }
    } catch (error) {
      console.error("Erro ao buscar saldo do cliente:", error);
    } finally {
      setIsBalanceModalOpen(true);
    }
  };

  const handleSort = (column: string) => {
    if (!onSort) return;

    const direction =
      sortConfig?.column === column && sortConfig?.direction === "asc"
        ? "desc"
        : "asc";

    onSort(column, direction);
  };

  const getSortIcon = (column: string) => {
    if (sortConfig?.column !== column) {
      return null;
    }
    return sortConfig?.direction === "asc" ? (
      <ChevronUp className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronDown className="w-4 h-4 inline ml-1" />
    );
  };

  const handleWhatsAppClick = (phone: string) => {
    const formattedPhone = phone.replace(/\D/g, "");
    window.open(`https://wa.me/55${formattedPhone}`, "_blank");
  };

  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead
              className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort("name")}
            >
              <div className="flex items-center gap-1">
                <Search className="h-3 w-3 text-gray-400" />
                Nome{getSortIcon("name")}
              </div>
            </TableHead>
            {showDiscountColumn && (
              <TableHead>Desconto</TableHead>
            )}
            <TableHead
              className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 text-center"
              onClick={() => handleSort("total_balance")}
            >
              <div className="flex items-center justify-center gap-1">
                <Search className="h-3 w-3 text-gray-400" />
                Saldo{getSortIcon("total_balance")}
              </div>
            </TableHead>
            <TableHead className="text-center">Vale Compra</TableHead>
            <TableHead className="text-center">Antigo/Novo</TableHead>
            <TableHead>Vendedor</TableHead>
            {!showDiscountColumn && (
              <TableHead className="text-center">Ativo</TableHead>
            )}
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data.map((item, index) => {
            return (
              <TableRow key={item?.id}>
                <TableCell className="truncate max-w-80">
                  {item?.name}
                </TableCell>
                {showDiscountColumn && (
                  <TableCell>
                    {item?.discount > 0 ? (
                      <span className="text-sm">
                        {item?.discount}% ({item?.discount_type?.name || "Não especificado"})
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </TableCell>
                )}
                <TableCell className="text-center">
                  {Mask.formatCurrency(item?.total_balance || 0)}
                </TableCell>
                <TableCell className="text-center">
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${
                      item?.has_purchase_voucher
                        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                        : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                    }`}
                  >
                    {item?.has_purchase_voucher ? "Sim" : "Não"}
                  </span>
                </TableCell>
                <TableCell className="text-center">
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${
                      item?.is_new
                        ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                        : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                    }`}
                  >
                    {item?.is_new ? "Novo" : "Antigo"}
                  </span>
                </TableCell>
                <TableCell className="text-center">
                  {item?.sellers?.length
                    ? item.sellers.map((seller) => seller?.user?.name).join(", ")
                    : item?.seller?.user?.name}
                </TableCell>
                {!showDiscountColumn && (
                  <TableCell>
                    <ComponentSwitchToggleStatus
                      defaultChecked={item?.is_active}
                      params={{
                        func: setStatusClients,
                        funcParams: item?.uuid,
                      }}
                      updateFront={() =>
                        updateFront((prev) => {
                          return {
                            ...prev,
                            data: prev?.data?.map((values) => {
                              if (values?.uuid === item?.uuid)
                                return {
                                  ...values,
                                  is_active: !values?.is_active,
                                };
                              return values;
                            }),
                          };
                        })
                      }
                    />
                  </TableCell>
                )}
                <TableCell className="flex justify-center">
                  <div className="flex gap-3 w-full justify-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-green-600 hover:text-green-800 hover:bg-green-100 dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-900"
                      onClick={() => handleWhatsAppClick(item.phone)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                      >
                        <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.729.729 0 0 0-.529.247c-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z" />
                      </svg>
                    </Button>
                    {authAllTypesUser(() => (
                      <>
                        <If
                          condition={hasPermission(
                            "create-client-balance-list"
                          )}
                        >
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() =>
                              handleOpenBalanceModal({
                                uuid: item.uuid,
                                name: item.name,
                                id: String(item.id),
                                has_purchase_voucher: item.has_purchase_voucher,
                              })
                            }
                          >
                            <Wallet className="h-4 w-4" />
                          </Button>
                        </If>
                      </>
                    ))}

                    <If condition={hasPermission("create-client-balance-list")}>
                      <AddDevolution
                        updateFront={() => {}}
                        refreshList={pageActions.refresh}
                        preSelectedClientId={item.id}
                        preSelectedClientName={item.name}
                      />
                    </If>
                    <If condition={hasPermission("show-client-details")}>
                      <ShowBtn
                        labels={{ title: "Visualizar Cliente" }}
                        params={{
                          func: getShowClients,
                          funcParams: item?.uuid,
                        }}
                        render={(responseApi: TableDataProps) => (
                          <ScrollArea className="h-[500px] pr-4">
                            <div className="w-full h-auto">
                              <div className="flex flex-col gap-3">
                                <Grid.Item name="Nome" flex={0}>
                                  {responseApi?.name}
                                </Grid.Item>
                                <Grid.Item name="Status" flex={0}>
                                  {responseApi?.is_active
                                    ? "Ativo"
                                    : "Inativo"}
                                </Grid.Item>
                                <Grid.Item name="Tipo de Cliente" flex={0}>
                                  <span
                                    className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${
                                      responseApi?.is_new
                                        ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                                        : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                                    }`}
                                  >
                                    {responseApi?.is_new ? "Novo" : "Antigo"}
                                  </span>
                                </Grid.Item>
                                <Grid.Item name="Categoria" flex={0}>
                                  {responseApi?.client_category?.name}
                                </Grid.Item>
                                <Grid.Item name="Saldo Total" flex={0}>
                                  <span
                                    className={
                                      responseApi?.total_balance &&
                                      responseApi?.total_balance < 0
                                        ? "text-red-500"
                                        : "text-green-500"
                                    }
                                  >
                                    {responseApi?.total_balance !==
                                    undefined
                                      ? Mask.formatCurrency(
                                          responseApi.total_balance
                                        )
                                      : "Saldo não disponível"}
                                  </span>
                                </Grid.Item>
                                {responseApi?.has_purchase_voucher && (
                                  <Grid.Item name="Vale Compra" flex={0}>
                                    <div className="flex flex-col gap-1">
                                      <div className="flex items-center gap-2">
                                        <span className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 px-2 py-0.5 rounded text-xs font-semibold">
                                          Aceita Vale Compra
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2 mt-1">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">
                                          Saldo:
                                        </span>
                                        <span
                                          className={`font-semibold ${
                                            (responseApi?.purchase_voucher_balance ?? 0) > 0
                                              ? "text-green-600 dark:text-green-400"
                                              : "text-gray-500"
                                          }`}
                                        >
                                          {Mask.formatCurrency(
                                            responseApi?.purchase_voucher_balance ?? 0
                                          )}
                                        </span>
                                      </div>
                                      {responseApi?.max_purchase_voucher_value && (
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm text-gray-600 dark:text-gray-400">
                                            Limite máximo:
                                          </span>
                                          <span className="font-semibold text-blue-600 dark:text-blue-400">
                                            {Mask.formatCurrency(
                                              responseApi.max_purchase_voucher_value
                                            )}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </Grid.Item>
                                )}
                                <Grid.Item name="Endereço" flex={0}>
                                  {responseApi?.addresses &&
                                  responseApi.addresses.length > 0 ? (
                                    <div className="flex flex-col gap-2">
                                      {responseApi.addresses.map(
                                        (address, index) => (
                                          <div
                                            key={address.id}
                                            className="border p-2 rounded"
                                          >
                                            <div className="flex items-center gap-2 mb-1">
                                              <span className="font-semibold">
                                                Endereço {index + 1}
                                              </span>
                                              {address.pivot.is_main ===
                                                1 && (
                                                <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded">
                                                  Principal
                                                </span>
                                              )}
                                            </div>
                                            {address.street ? `${address.street}${address.number ? `, ${address.number}` : ''}` : address.city || 'Endereço não informado'}
                                          </div>
                                        )
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-gray-500">
                                      Nenhum endereço cadastrado
                                    </span>
                                  )}
                                </Grid.Item>
                                <Grid.Item name="Documento" flex={0}>
                                  {Mask.maskCpfOrCnpj(
                                    responseApi?.document
                                  )}
                                </Grid.Item>
                                <Grid.Item name="Telefone" flex={0}>
                                  <div className="flex flex-col gap-1">
                                    <span>{Mask.maskPhone(responseApi?.phone)}</span>
                                    {responseApi?.phone_2 && (
                                      <span>{Mask.maskPhone(responseApi?.phone_2)}</span>
                                    )}
                                  </div>
                                </Grid.Item>
                                <Grid.Item name="Email" flex={0}>
                                  {responseApi?.email}
                                </Grid.Item>
                                <Grid.Item name="Origem" flex={0}>
                                  <div className="flex items-center gap-2">
                                    <span>{responseApi?.client_origin?.name}</span>
                                    {responseApi?.client_origin_photo && (
                                      <a
                                        href={responseApi.client_origin_photo}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 text-sm"
                                      >
                                        <ExternalLink className="h-4 w-4" />
                                        Ver foto
                                      </a>
                                    )}
                                  </div>
                                </Grid.Item>
                                <Grid.Item name="Desconto" flex={0}>
                                  {responseApi?.discount}% (
                                  {responseApi?.discount_type?.name})
                                </Grid.Item>
                              </div>
                            </div>
                          </ScrollArea>
                        )}
                        loader={<CreateLoaderInput lines={8} />}
                      />
                    </If>
                    {authClientAndUnityResponsible(() => (
                      <>
                        <If condition={hasPermission("edit-client")}>
                          <UpdateBtn
                            uuid={item?.uuid}
                            updateFront={(response) => {
                              updateFront((prev) => {
                                prev?.data?.splice(index, 1, response);
                                return {
                                  ...prev,
                                  data: [...prev.data],
                                };
                              });
                            }}
                          />
                        </If>
                        <If condition={hasPermission("delete-client")}>
                          <DeleteBtn
                            params={{
                              func: deleteClientsData,
                              funcParams: item?.uuid,
                            }}
                            updateFront={() =>
                              updateFront((prev) => ({
                                ...prev,
                                data: [
                                  ...prev?.data?.filter(
                                    (data) => data?.uuid !== item?.uuid
                                  ),
                                ],
                              }))
                            }
                            referenceLabel={item?.name}
                          />
                        </If>
                      </>
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
          {dataApi?.data.length < 1 && (
            <TableRow>
              <TableCell colSpan={7}>Nada foi encontrado.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex justify-between mb-3">
        {dataApi?.data.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>

      {selectedClient && (
        <BalanceModal
          open={isBalanceModalOpen}
          onClose={() => setIsBalanceModalOpen(false)}
          clientId={selectedClient?.id}
          clientName={selectedClient.name}
          currentBalance={selectedClient.total_balance}
        />
      )}
    </>
  );
};
